/**
 * Date-range helpers shared by the list pages' filters.
 *
 * The pages keep their date filters as calendar days ('YYYY-MM-DD', see
 * `formatDate`). `new Date('YYYY-MM-DD')` parses that as UTC midnight, which
 * is the wrong day boundary outside UTC, so plain day strings are read as a
 * LOCAL calendar day here. Any other value (a Date, a full ISO timestamp)
 * goes through `new Date()` and its local day is used.
 */
const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const toLocalDate = (value: string | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }
  const dayOnly = DAY_ONLY.exec(String(value).trim());
  const date = dayOnly
    ? new Date(Number(dayOnly[1]), Number(dayOnly[2]) - 1, Number(dayOnly[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Whole local days as an ISO range: [from 00:00:00.000, to 23:59:59.999].
 * Returns undefined when either bound is missing or invalid, so callers can
 * skip the date condition. Never mutates its arguments.
 */
export const toDayRangeIso = (
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
): [string, string] | undefined => {
  const fromDate = toLocalDate(from);
  const toDate = toLocalDate(to);
  if (!fromDate || !toDate) return undefined;
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);
  return [fromDate.toISOString(), toDate.toISOString()];
};

/**
 * Human-readable Africa/Cairo date & time for a stored ISO/UTC timestamp,
 * e.g. '2026-10-21T16:58:25.174Z' -> '21 Oct 2026, 7:58 PM'.
 * Returns '' for empty input and the original value if it can't be parsed.
 * Display only: keep sorting/filtering on the raw value.
 */
export const formatCairoDateTime = (
  value: string | Date | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const parts: Record<string, string> = {};
  new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Cairo',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .formatToParts(date)
    .forEach((part) => {
      parts[part.type] = part.value;
    });
  return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${
    parts.minute
  } ${(parts.dayPeriod || '').toUpperCase()}`.trim();
};
